import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Ctx } from '../ctx';
import { ask } from '../dialogs';
import { ErrorBanner, Pill } from '../ui';
import { readUpload, saveBlob, sizeLabel } from '../files';
import { SecurityProfileModal } from './SecurityProfile';

export type Kind = 'verifica' | 'azione' | 'documento' | 'domanda';
export type Status = 'da_fare' | 'in_corso' | 'in_attesa' | 'fatto';
export interface Task {
  id: number;
  key: string;
  kind: Kind;
  priority: string;
  title: string;
  reason: string;
  instructions: string;
  owner: string;
  status: Status;
  evidence: string;
  fileCount: number;
  updatedAt: string;
}
export interface Plan { clientName: string | null; importedAt: string | null; tasks: Task[] }
interface GeneratedPlan {
  plan: { cliente: string; azioni: unknown[]; documenti: unknown[]; domande: unknown[] };
  html: string;
  reportCount: number;
}
interface ProofFile { id: number; name: string; size: number; sha256: string; createdAt: string }
export type Workspace = 'overview' | 'guide' | 'acn' | 'assets' | 'testing' | 'siem' | 'training' | 'incidents' | 'documents' | 'mail' | 'sonda' | 'devices' | 'antivirus' | 'settings';

const tabs: { kind: Kind; title: string }[] = [
  { kind: 'verifica', title: 'Verifiche' },
  { kind: 'azione', title: 'Lavori' },
  { kind: 'documento', title: 'Documenti' },
  { kind: 'domanda', title: 'Da chiedere' },
];
export const statusLabel: Record<Status, string> = { da_fare: 'Da fare', in_corso: 'In corso', in_attesa: 'In attesa', fatto: 'Fatto' };
export const gateKeys = ['verifica:ambito', 'verifica:autorizzazione', 'verifica:privacy-lavoro'];
export const finalKeys = ['verifica:ricontrollo', 'verifica:pentest'];

export function taskStage(task: Task): number {
  if (gateKeys.includes(task.key)) return 0;
  if (task.kind === 'domanda') return 1;
  if (task.key === 'verifica:iniziale') return 2;
  if (task.kind === 'azione' && task.priority === 'Urgente') return 3;
  if (task.kind === 'azione' && task.priority === 'Importante') return 4;
  if (task.kind === 'documento') return 5;
  if (finalKeys.includes(task.key)) return 6;
  return 7;
}
export const stageNames = ['Preparare', 'Completare le informazioni', 'Controllare', 'Risolvere le urgenze', 'Mettere in sicurezza', 'Preparare i documenti', 'Ricontrollare', 'Programmare'];

export function taskTool(task: Task): { view: Workspace; label: string } | null {
  if (task.key === 'verifica:ambito') return { view: 'assets', label: 'Apri inventario' };
  if (['verifica:iniziale', 'verifica:ricontrollo', 'verifica:pentest', 'azione:vulnerabilita', 'azione:dominio_da_controllare'].includes(task.key)) return { view: 'testing', label: 'Apri verifiche' };
  if (task.key === 'azione:siem') return { view: 'siem', label: 'Apri monitoraggio' };
  if (['azione:antivirus_gestito', 'azione:antivirus_spento'].includes(task.key)) return { view: 'antivirus', label: 'Apri Antivirus' };
  if (['azione:guard', 'azione:edr_copertura'].includes(task.key)) return { view: 'devices', label: 'Apri PC' };
  return null;
}

export function SecurityPlan({ ctx, siteId, siteName, onNavigate }: { ctx: Ctx; siteId: number; siteName: string; onNavigate: (view: Workspace) => void }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [tab, setTab] = useState<Kind>('verifica');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const profileInput = useRef<HTMLInputElement>(null);
  const reportsInput = useRef<HTMLInputElement>(null);
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [savedProfile, setSavedProfile] = useState<string | null>(null);
  const [reportFiles, setReportFiles] = useState<File[]>([]);
  const [generated, setGenerated] = useState<GeneratedPlan | null>(null);
  const [editProfile, setEditProfile] = useState(false);

  useEffect(() => { reportsInput.current?.setAttribute('webkitdirectory', ''); }, []);
  useEffect(() => {
    setProfileFile(null);
    setSavedProfile(null);
    setReportFiles([]);
    setGenerated(null);
    if (profileInput.current) profileInput.current.value = '';
    if (reportsInput.current) reportsInput.current.value = '';
  }, [siteId]);

  const load = useCallback(async () => {
    try {
      const [current, saved] = await Promise.all([
        ctx.api.get<Plan>(`/api/guard/sites/${siteId}/plan`),
        ctx.api.get<{ profile: { cliente: string }; updatedAt: string } | null>(`/api/guard/sites/${siteId}/plan/profile`),
      ]);
      setPlan(current);
      setSavedProfile(saved?.profile.cliente ?? null);
    }
    catch (e) { setError((e as Error).message); }
  }, [ctx.api, siteId]);
  useEffect(() => { void load(); }, [load]);

  const importFile = async (file?: File) => {
    if (!file) return;
    setError(null);
    if (file.size > 1_000_000) { setError('Il file è troppo grande. Scegli il piano JSON creato da Tecnoside Piano.'); return; }
    try {
      const raw = JSON.parse(await file.text()) as Record<string, unknown>;
      if (typeof raw.cliente !== 'string' || !Array.isArray(raw.azioni) || !Array.isArray(raw.documenti) || !Array.isArray(raw.domande)) {
        throw new Error('Questo non è un piano Tecnoside valido.');
      }
      if (!(await ask(`Importare il piano di «${raw.cliente}» nel fascicolo «${siteName}»? Le note e gli stati già salvati restano.`))) return;
      setBusy(true);
      const payload = { cliente: raw.cliente, azioni: raw.azioni, documenti: raw.documenti, domande: raw.domande, ...(raw.misure_acn ? { misure_acn: raw.misure_acn } : {}) };
      setPlan(await ctx.api.post<Plan>(`/api/guard/sites/${siteId}/plan`, payload));
      setTab('verifica');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const save = async (task: Task, status: Status, evidence: string) => {
    setError(null);
    try {
      setPlan(await ctx.api.request<Plan>('PATCH', `/api/guard/sites/${siteId}/plan/tasks/${task.id}`, { status, evidence }));
    } catch (e) { setError((e as Error).message); throw e; }
  };

  const generate = async () => {
    if (!profileFile || reportFiles.length === 0) return;
    setError(null);
    setGenerated(null);
    setBusy(true);
    try {
      if (profileFile.size > 100_000 || reportFiles.length > 100 || reportFiles.some((f) => f.size > 1_000_000)
        || reportFiles.reduce((sum, file) => sum + file.size, 0) > 8_000_000) {
        throw new Error('Il profilo o uno dei report è troppo grande.');
      }
      const profile = JSON.parse(await profileFile.text()) as Record<string, unknown>;
      const reports = await Promise.all(reportFiles.map((f) => f.text()));
      const result = await ctx.api.post<GeneratedPlan>(`/api/guard/sites/${siteId}/plan/generate`, { profile, reports });
      setGenerated(result);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const generateFromDevices = async () => {
    setError(null);
    setGenerated(null);
    setBusy(true);
    try {
      if (profileFile) {
        if (profileFile.size > 100_000) throw new Error('Il profilo del cliente è troppo grande.');
        const profile = JSON.parse(await profileFile.text()) as Record<string, unknown>;
        const saved = await ctx.api.request<{ profile: { cliente: string } }>('PUT', `/api/guard/sites/${siteId}/plan/profile`, profile);
        setSavedProfile(saved.profile.cliente);
      }
      const result = await ctx.api.post<GeneratedPlan>(`/api/guard/sites/${siteId}/plan/generate-from-devices`, {});
      setGenerated(result);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const useGenerated = async () => {
    if (!generated) return;
    if (!(await ask(`Usare il piano di «${generated.plan.cliente}» per «${siteName}»? Le note e i lavori già completati restano.`))) return;
    setError(null);
    setBusy(true);
    try {
      setPlan(await ctx.api.post<Plan>(`/api/guard/sites/${siteId}/plan`, generated.plan));
      setGenerated(null);
      setTab('verifica');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const tasks = plan?.tasks ?? [];
  const done = tasks.filter((t) => t.status === 'fatto').length;
  const required = tasks.filter((t) => gateKeys.includes(t.key));
  const pending = tasks.filter((t) => t.status !== 'fatto' || (required.some((r) => r.id === t.id) && t.key !== 'verifica:ambito' && t.fileCount === 0));
  const ordered = [...pending].sort((a, b) => taskStage(a) - taskStage(b) || gateKeys.indexOf(a.key) - gateKeys.indexOf(b.key) || a.id - b.id);
  const gates = ordered.filter((t) => gateKeys.includes(t.key));
  const pendingGate = gates.find((t) => t.status !== 'in_attesa') ?? gates[0];
  const next = pendingGate ?? ordered.find((t) => t.status !== 'in_attesa') ?? ordered[0];
  const nextTool = next ? taskTool(next) : null;
  return (
    <section style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 18, margin: '20px 0', background: 'var(--white)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 className="guard-h grow" style={{ margin: 0 }}>Guida alla sicurezza</h3>
        {tasks.length > 0 && <Pill tone="info">{done} di {tasks.length} fatti</Pill>}
      </div>
      <p className="muted small" style={{ margin: '8px 0 14px' }}>
        {plan?.importedAt ? `${tasks.length <= 3 ? 'Preparazione' : 'Piano'} di ${plan.clientName} · aggiornato il ${new Date(plan.importedAt).toLocaleDateString('it-IT')}. Apri una voce per segnare il lavoro e la prova.` : 'Crea il piano dai report per vedere lavori, documenti e verifiche in un solo posto.'}
      </p>
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <strong>Crea il piano dai controlli</strong>
        <p className="muted small" style={{ margin: '6px 0 10px' }}>Usa i report inviati dai PC collegati. Controlla il piano prima di usarlo.</p>
        <input ref={profileInput} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={(e) => { setProfileFile(e.target.files?.[0] ?? null); setGenerated(null); }} />
        <input ref={reportsInput} type="file" multiple style={{ display: 'none' }} onChange={(e) => {
          const reports = Array.from(e.target.files ?? []).filter((f) => f.name.toLowerCase() === 'report.json');
          setReportFiles(reports);
          setGenerated(null);
          if (e.target.files?.length && !reports.length) setError('La cartella scelta non contiene file report.json di Assessment.');
        }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn small" title="Le domande sull'azienda: dimensioni, NIS2, voucher, cosa hanno già" onClick={() => setEditProfile(true)}>
            {profileFile ? `Nuovo profilo: ${profileFile.name}` : savedProfile ? `Profilo: ${savedProfile}` : 'Compila il profilo'}
          </button>
          <button type="button" className="btn dark small" disabled={busy || (!profileFile && !savedProfile)} onClick={() => void generateFromDevices()}>{busy ? 'Creo il piano…' : 'Crea dai PC collegati'}</button>
        </div>
        {editProfile && (
          <SecurityProfileModal
            ctx={ctx}
            siteId={siteId}
            siteName={siteName}
            onClose={() => setEditProfile(false)}
            onSaved={(cliente) => { setSavedProfile(cliente); setProfileFile(null); setGenerated(null); setEditProfile(false); }}
          />
        )}
        {generated && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--white)' }}>
          <strong>{generated.plan.cliente}</strong>
          <p className="muted small" style={{ margin: '5px 0 10px' }}>{generated.reportCount} report letti · {generated.plan.azioni.length} lavori · {generated.plan.documenti.length} documenti</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn small" onClick={() => saveBlob(new Blob([generated.html], { type: 'text/html;charset=utf-8' }), 'piano-sicurezza.html')}>Scarica il piano completo</button>
            <button type="button" className="btn dark small" disabled={busy} onClick={() => void useGenerated()}>Usa questo piano</button>
          </div>
        </div>}
        <details style={{ marginTop: 12 }}>
          <summary className="small">Hai report salvati sul computer?</summary>
          <p className="muted small" style={{ margin: '8px 0' }}>Usali se i PC non sono ancora collegati. Scegli il profilo e la cartella dei report.</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn small" onClick={() => profileInput.current?.click()}>{profileFile ? `Profilo: ${profileFile.name}` : 'Scegli il profilo'}</button>
            <button type="button" className="btn small" onClick={() => reportsInput.current?.click()}>{reportFiles.length ? `${reportFiles.length} report scelti` : 'Scegli la cartella dei report'}</button>
            <button type="button" className="btn small" disabled={busy || !profileFile || reportFiles.length === 0} onClick={() => void generate()}>{busy ? 'Creo il piano…' : 'Crea dai file'}</button>
          </div>
        </details>
        <details style={{ marginTop: 12 }}>
          <summary className="small">Hai già un piano JSON?</summary>
          <input ref={fileInput} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={(e) => void importFile(e.target.files?.[0])} />
          <button type="button" className="btn small" style={{ marginTop: 8 }} disabled={busy} onClick={() => fileInput.current?.click()}>
            {busy ? 'Importazione…' : 'Importa il piano esistente'}
          </button>
        </details>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {next && <div className="small" style={{ margin: '0 0 14px', padding: '16px', border: '1px solid var(--line)', borderRadius: 8 }}>
        <div className="muted small">Passo {done + 1} di {tasks.length} · {stageNames[taskStage(next)]}</div>
        {next.status === 'in_attesa' && <div className="muted" style={{ marginTop: 6 }}>In attesa di risposta o documento.</div>}
        <div style={{ marginTop: 10 }}><TaskRow key={next.id} task={next} onSave={save} ctx={ctx} siteId={siteId} onUploaded={load} onError={setError} focused /></div>
        {nextTool && <button type="button" className="btn small" onClick={() => onNavigate(nextTool.view)}>{nextTool.label}</button>}
      </div>}
      {tasks.length > 0 && !next && <p className="small">Tutte le attività del piano risultano completate. Controlla gli avvisi e le verifiche periodiche.</p>}
      {tasks.length > 0 && <>
        <button type="button" className="btn ghost small" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)} style={{ marginBottom: 12 }}>
          {showAll ? 'Nascondi tutte le attività' : `Vedi tutte le attività (${tasks.length})`}
        </button>
      {showAll && <>
        <div role="tablist" aria-label="Parti del piano" style={{ display: 'flex', gap: 5, borderBottom: '1px solid var(--line)', overflowX: 'auto', marginBottom: 12 }}>
          {tabs.map((t) => {
            const group = tasks.filter((x) => x.kind === t.kind);
            return <button key={t.kind} type="button" role="tab" aria-selected={tab === t.kind} className={`btn small ${tab === t.kind ? 'dark' : 'ghost'}`} onClick={() => setTab(t.kind)} style={{ marginBottom: 7, whiteSpace: 'nowrap' }}>
              {t.title} · {group.filter((x) => x.status === 'fatto').length}/{group.length}
            </button>;
          })}
        </div>
        <div role="tabpanel">
          {tasks.filter((t) => t.kind === tab).map((t) => <TaskRow key={t.id} task={t} onSave={save} ctx={ctx} siteId={siteId} onUploaded={load} onError={setError} />)}
        </div>
      </>}
      </>}
    </section>
  );
}

function TaskRow({ task, onSave, ctx, siteId, onUploaded, onError, focused = false }: {
  task: Task;
  onSave: (task: Task, status: Status, evidence: string) => Promise<void>;
  ctx: Ctx;
  siteId: number;
  onUploaded: () => Promise<void>;
  onError: (message: string | null) => void;
  focused?: boolean;
}) {
  const [status, setStatus] = useState<Status>(task.status);
  const [evidence, setEvidence] = useState(task.evidence);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<ProofFile[] | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => { setStatus(task.status); setEvidence(task.evidence); }, [task.status, task.evidence]);
  const filePath = `/api/guard/sites/${siteId}/plan/tasks/${task.id}/files`;
  const loadFiles = async () => {
    try { setFiles(await ctx.api.get<ProofFile[]>(filePath)); }
    catch (error) { onError((error as Error).message); }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true); onError(null);
    try {
      const body = await readUpload(file);
      await ctx.api.post(filePath, { name: body.name, mime: body.mime, contentBase64: body.contentBase64 });
      await Promise.all([loadFiles(), onUploaded()]);
    } catch (error) { onError((error as Error).message); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ''; }
  };
  const download = async (file: ProofFile) => {
    try { saveBlob(await ctx.api.download(`/api/guard/sites/${siteId}/plan/files/${file.id}`), file.name); }
    catch (error) { onError((error as Error).message); }
  };
  const changed = status !== task.status || evidence !== task.evidence;
  return <details id={focused ? undefined : `security-task-${siteId}-${task.id}`} open={focused ? true : undefined} onToggle={(event) => { if (event.currentTarget.open && files === null) void loadFiles(); }} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', marginBottom: 7, background: 'var(--white)' }}>
    <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}>
      <b style={{ flex: 1 }}>{task.title}</b>
      <span className="muted small">{statusLabel[task.status]}{task.fileCount > 0 ? ` · ${task.fileCount} prove` : ''}</span>
    </summary>
    <div style={{ padding: '10px 2px 2px', maxWidth: 760 }}>
      {task.reason && <p className="small" style={{ margin: '0 0 8px' }}>{task.reason}</p>}
      {task.instructions && <p className="small" style={{ margin: '0 0 8px' }}><b>Cosa fare:</b> {task.instructions}</p>}
      {task.owner && <p className="small muted" style={{ margin: '0 0 12px' }}>Chi: {task.owner}{task.priority ? ` · ${task.priority}` : ''}</p>}
      <label className="small" style={{ display: 'block', marginBottom: 8 }}>Stato<br />
        <select value={status} onChange={(e) => setStatus(e.target.value as Status)} style={{ marginTop: 4 }}>
          {Object.entries(statusLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="small" style={{ display: 'block' }}>Prova o nota<br />
        <textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={3} maxLength={8000} placeholder="Che cosa è stato fatto? Dove si trova la prova?" style={{ width: '100%', marginTop: 4 }} />
      </label>
      {status === 'fatto' && task.fileCount === 0 && (task.key === 'verifica:autorizzazione' || task.key === 'verifica:privacy-lavoro') && <p className="small">Carica prima il documento qui sotto.</p>}
      <button type="button" className="btn dark small" disabled={busy || !changed || (status === 'fatto' && evidence.trim().length < 5) || (status === 'fatto' && task.fileCount === 0 && (task.key === 'verifica:autorizzazione' || task.key === 'verifica:privacy-lavoro'))} onClick={() => { setBusy(true); void onSave(task, status, evidence.trim()).finally(() => setBusy(false)); }} style={{ marginTop: 8 }}>
        {busy ? 'Salvataggio…' : 'Salva'}
      </button>
      <div style={{ borderTop: '1px solid var(--line)', marginTop: 16, paddingTop: 12 }}>
        <b className="small">Prove e report</b>
        <p className="muted small" style={{ margin: '4px 0 8px' }}>Carica il report o il verbale che dimostra il lavoro. Solo gli amministratori Tecnoside possono aprirlo.</p>
        <input ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.json,.html" style={{ display: 'none' }} onChange={(event) => void upload(event.target.files?.[0])} />
        <button type="button" className="btn small" disabled={busy} onClick={() => fileInput.current?.click()}>Carica una prova</button>
        {files?.map((file) => <div key={file.id} className="small" style={{ marginTop: 8 }}>
          <button type="button" className="btn ghost small" onClick={() => void download(file)}>{file.name}</button>
          <span className="muted"> {sizeLabel(file.size)} · {new Date(file.createdAt).toLocaleDateString('it-IT')}</span>
        </div>)}
      </div>
    </div>
  </details>;
}
